import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BookOpen,
  Camera,
  Search,
  ShoppingBag,
  Tag,
  Shield,
  Sparkles,
  Users,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Package,
  MessageCircle,
  MapPin,
  CheckCircle,
  Clock,
  X,
  ArrowRight,
  Lightbulb,
  Star
} from 'lucide-react';

// Guide content organized by section
const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    guides: [
      { id: 'welcome', title: 'Welcome to WANTD', isNew: true },
      { id: 'how-it-works', title: 'How WANTD Works' },
      { id: 'account-setup', title: 'Setting Up Your Account' },
    ]
  },
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: Sparkles,
    badge: 'Premium',
    guides: [
      { id: 'ai-photo-scanner', title: 'Using AI Photo Scanner', isNew: true },
      { id: 'ai-matching', title: 'How AI Matching Works' },
    ]
  },
  {
    id: 'selling',
    title: 'Selling Items',
    icon: Tag,
    guides: [
      { id: 'add-inventory', title: 'Listing Items for Sale' },
      { id: 'great-listings', title: 'Creating Listings That Sell' },
      { id: 'pricing-tips', title: 'Pricing Your Items' },
    ]
  },
  {
    id: 'buying',
    title: 'Buying Items',
    icon: ShoppingBag,
    guides: [
      { id: 'browsing-catalog', title: 'Browsing the Catalog' },
      { id: 'create-want', title: 'Creating a Want Listing' },
    ]
  },
  {
    id: 'safety',
    title: 'Safety & Trust',
    icon: Shield,
    guides: [
      { id: 'meetup-safety', title: 'Safe Meetup Tips' },
      { id: 'trust-scores', title: 'Understanding Trust Scores' },
      { id: 'report-issues', title: 'Reporting Issues' },
    ]
  },
  {
    id: 'faq',
    title: 'FAQ',
    icon: HelpCircle,
    guides: [
      { id: 'general-faq', title: 'General Questions' },
      { id: 'account-faq', title: 'Account & Privacy' },
    ]
  }
];

// Full guide content
const GUIDE_CONTENT = {
  // Getting Started
  'welcome': {
    title: 'Welcome to WANTD',
    subtitle: 'Your guide to getting the most out of your first 7 days',
    icon: Star,
    sections: [
      {
        title: 'What is WANTD?',
        content: `WANTD is a local marketplace that works differently. Instead of just browsing listings, you can tell us what you're looking for and we'll match you with sellers nearby.

**Two ways to use WANTD:**
- **Browse & Buy** - Explore items listed by sellers in your area
- **Post & Match** - Tell us what you want, and we'll find it for you`
      },
      {
        title: 'Your 7-Day Premium Trial',
        content: `As a new member, you have **free access to all Premium features** for your first 7 days:

- **AI Photo Scanner** - Snap photos, we'll create your listings automatically
- **Smart Matching** - Get priority notifications when items match your wants
- **Premium Badge** - Stand out to sellers

Make the most of it! Try the AI Photo Scanner to quickly list items from your home.`,
        highlight: true
      },
      {
        title: 'Quick Start Checklist',
        content: `Get set up in 5 minutes:

1. **Set your location** - We'll show you items nearby
2. **Try the AI Scanner** - Add 3-5 items from your home
3. **Post your first Want** - Tell us something you're looking for
4. **Browse the catalog** - See what's available near you`
      },
      {
        title: 'Next Steps',
        links: [
          { text: 'Learn how to use the AI Photo Scanner', guide: 'ai-photo-scanner' },
          { text: 'Tips for creating great listings', guide: 'great-listings' },
          { text: 'How to create effective Want listings', guide: 'create-want' }
        ]
      }
    ]
  },
  
  'how-it-works': {
    title: 'How WANTD Works',
    subtitle: 'Understanding the basics of buying and selling',
    icon: BookOpen,
    sections: [
      {
        title: 'For Buyers',
        content: `**Option 1: Browse the Catalog**
Just like any marketplace - browse items and message sellers.

**Option 2: Post What You Want**
Create a "Want" listing with:
- What you're looking for
- Your price range
- How flexible you are on specs
- Your location

Our system automatically matches your want with sellers' inventory and notifies both of you when there's a match.`
      },
      {
        title: 'For Sellers',
        content: `**Step 1: List Your Items**
List items you'd consider selling. Use the AI Photo Scanner to add items quickly - just take photos!

**Step 2: Set Your Prices**
Choose firm prices or mark as negotiable. You control the terms.

**Step 3: Get Matched**
When a buyer's "Want" matches your item, you'll be notified. Review the match and decide if you're interested.

**WANTD is free for all users** - no listing fees and no marketplace fees.`
      },
      {
        title: 'The Matching System',
        content: `Our AI considers multiple factors when matching:
- **Category & Keywords** - What the item is
- **Price Overlap** - Your budget vs asking price
- **Location** - How close you are to each other
- **Condition** - Does it meet the buyer's requirements?
- **Trust Scores** - Reliability of both parties`
      }
    ]
  },

  'account-setup': {
    title: 'Setting Up Your Account',
    subtitle: 'Complete your profile for better matches',
    icon: Users,
    sections: [
      {
        title: 'Profile Basics',
        content: `A complete profile helps build trust:

- **Profile Photo** - Adds a personal touch
- **Location** - Required for matching (only suburb shown publicly)
- **Username** - How others will identify you`
      },
      {
        title: 'Location Settings',
        content: `Your location is crucial for local matching:

1. Go to **Profile** > **Edit Profile**
2. Enter your suburb or use "Detect Location"
3. We only show your suburb name - never your exact address

**Tip:** Setting an accurate location means better, more relevant matches.`
      }
    ]
  },

  // AI Features
  'ai-photo-scanner': {
    title: 'Using the AI Photo Scanner',
    subtitle: 'The fastest way to list items from your home',
    icon: Camera,
    badge: 'Premium Feature',
    sections: [
      {
        title: 'What is the AI Photo Scanner?',
        content: `The AI Photo Scanner is a premium feature that automatically detects items in your photos and creates listing suggestions. Just snap a photo of your room, garage, or closet - our AI will identify sellable items and create draft listings for you.

**Perfect for:**
- Quickly listing multiple items
- Decluttering your home
- Getting started when you're not sure what to sell`
      },
      {
        title: 'How to Use It',
        content: `**Step 1: Access the Scanner**
Go to **Sell** > **Auto-List from Photo(s)** (or tap the camera icon)

**Step 2: Take Photos**
- Photograph rooms, shelves, or individual items
- Good lighting helps accuracy
- Multiple angles work best for single items

**Step 3: Review Suggestions**
The AI will detect items and suggest:
- Title and description
- Category
- Estimated value
- Condition assessment

**Step 4: Approve or Edit**
- Accept suggestions as-is
- Edit details before saving
- Reject items you don't want to sell`
      },
      {
        title: 'Tips for Best Results',
        content: `**Do:**
- Use good lighting (natural light is best)
- Keep items uncluttered in frame
- Include multiple angles for valuable items
- Let the AI see brand names and labels

**Don't:**
- Photograph in dark rooms
- Pile items on top of each other
- Expect perfect results for very old/obscure items`
      },
      {
        title: 'What the AI Detects',
        content: `The scanner works best with:
- Electronics (phones, laptops, TVs, gaming consoles)
- Furniture (chairs, tables, shelves)
- Clothing and shoes (especially branded items)
- Tools and equipment
- Sports gear
- Books and media
- Home appliances

For rare or vintage items, you may need to add details manually.`
      }
    ]
  },

  'ai-matching': {
    title: 'How AI Matching Works',
    subtitle: 'Understanding how we connect buyers and sellers',
    icon: Sparkles,
    sections: [
      {
        title: 'The Matching Algorithm',
        content: `When you post a "Want" or list an item, our AI constantly scans for potential matches based on:

1. **Semantic Matching** - Understanding what items actually are, not just keywords
2. **Price Compatibility** - Finding overlap between budgets and asking prices
3. **Location Proximity** - Prioritizing nearby matches
4. **Condition Requirements** - Matching buyer preferences with item condition
5. **Trust Scores** - Factoring in user reliability`
      },
      {
        title: 'Getting Better Matches',
        content: `**For Buyers:**
- Be specific in your "Want" descriptions
- Set realistic price ranges
- Indicate flexibility on condition

**For Sellers:**
- Write detailed, accurate descriptions
- Use the AI scanner for consistent listings
- Price items fairly based on condition`
      }
    ]
  },

  // Selling
  'add-inventory': {
    title: 'Listing Items for Sale',
    subtitle: 'List items for sale in minutes',
    icon: Package,
    sections: [
      {
        title: 'Two Ways to List Items',
        content: `**1. AI Photo Scanner (Fastest)**
Take photos and let AI create listings for you. Perfect for multiple items.

**2. Manual Entry**
Fill out the listing form yourself. Better for detailed, high-value items.`
      },
      {
        title: 'What Makes a Good Listing',
        content: `**Required:**
- Clear photos (at least one)
- Accurate title
- Category selection
- Price or "Negotiable"
- Location

**Recommended:**
- Multiple photos from different angles
- Honest condition assessment
- Detailed description
- Brand/model if applicable`
      },
      {
        title: 'Listing Status',
        content: `Your items can be:
- **Active** - Visible in marketplace, available for matching
- **Inactive** - Hidden from searches, kept in your items
- **Sold** - Marked after successful transaction`
      }
    ]
  },

  'great-listings': {
    title: 'Creating Listings That Sell',
    subtitle: 'Tips from successful sellers',
    icon: Star,
    sections: [
      {
        title: 'Photos That Sell',
        content: `**The first photo is everything.** Make it count:

- Clean, uncluttered background
- Good lighting (natural light is best)
- Show the full item in the first photo
- Add detail shots for condition/features
- Include photos of any flaws (builds trust)`
      },
      {
        title: 'Writing Great Titles',
        content: `**Good titles include:**
- Brand name (if applicable)
- Model/product name
- Key feature or condition
- Size (for clothing/furniture)

**Examples:**
- ✅ "Apple MacBook Pro 14" M3 - Excellent Condition"
- ✅ "IKEA KALLAX Shelf Unit - White, 4x4"
- ❌ "Laptop for sale"
- ❌ "Shelf"`
      },
      {
        title: 'Pricing Strategy',
        content: `- **Research similar items** - Check what others are asking
- **Consider condition** - Be honest, price accordingly
- **Factor in age** - Older items generally sell for less
- **Leave room for negotiation** - If you're flexible, price slightly higher

**Tip:** Items priced fairly sell faster than overpriced items that sit for weeks.`
      }
    ]
  },

  'pricing-tips': {
    title: 'Pricing Your Items',
    subtitle: 'Find the sweet spot for faster sales',
    icon: Tag,
    sections: [
      {
        title: 'Research Before Pricing',
        content: `Before setting a price:
1. Search for similar items on WANTD
2. Check completed sales on other platforms
3. Consider the original retail price
4. Factor in condition and age`
      },
      {
        title: 'Firm vs Negotiable',
        content: `**Firm Price:**
- You won't accept a lower price
- Good for fairly-priced items
- Shows confidence in your pricing

**Negotiable:**
- Open to discussing the price
- Attracts more interest
- May sell faster

**Tip:** Most items sell better as "Negotiable" - it invites conversation.`
      }
    ]
  },

  // Buying
  'browsing-catalog': {
    title: 'Browsing the Catalog',
    subtitle: 'Find what you\'re looking for',
    icon: Search,
    sections: [
      {
        title: 'Using Filters',
        content: `Narrow down results with:
- **Category** - Electronics, Furniture, Clothing, etc.
- **Price Range** - Set your budget
- **Location** - Distance from you
- **Condition** - New, Like New, Good, Fair

**Tip:** Start broad, then narrow down. You might find something you didn't expect!`
      },
      {
        title: 'Understanding Listings',
        content: `Each listing shows:
- **Photos** - Tap to see more
- **Price** - Or "Negotiable" if flexible
- **Condition** - Seller's assessment
- **Distance** - How far from you
- **Seller Info** - Username and trust score`
      }
    ]
  },

  'create-want': {
    title: 'Creating a Want Listing',
    subtitle: 'Let sellers come to you',
    icon: ShoppingBag,
    sections: [
      {
        title: 'What is a Want Listing?',
        content: `Instead of searching, tell us what you want and we'll find it for you. When a seller's item matches your criteria, you'll both be notified.

**Great for:**
- Specific items that are hard to find
- When you don't want to constantly search
- Niche or rare items`
      },
      {
        title: 'Creating an Effective Want',
        content: `**Be Specific:**
- Include brand/model if you know it
- Describe the item clearly
- Mention must-have features

**Set Your Budget:**
- Min/Max price range
- Be realistic - too low won't match

**Condition Preferences:**
- Which conditions will you accept?
- More flexibility = more matches

**Location:**
- How far are you willing to travel?
- Wider radius = more options`
      },
      {
        title: 'Managing Your Wants',
        content: `- **Active Wants** - Continuously matching
- **Pause** - Temporarily stop matching
- **Edit** - Update criteria anytime
- **Delete** - Remove when no longer needed

**Tip:** Review your wants periodically. Old wants with no matches might need updated criteria.`
      }
    ]
  },

  // Safety
  'meetup-safety': {
    title: 'Safe Meetup Tips',
    subtitle: 'Stay safe when meeting in person',
    icon: Shield,
    sections: [
      {
        title: 'Choosing a Location',
        content: `**Best options:**
- Public places (shopping centers, cafes)
- Police station parking lots
- Busy areas during daylight

**Avoid:**
- Private residences (for first meetings)
- Isolated areas
- Late night meetups`
      },
      {
        title: 'During the Meetup',
        content: `- **Tell someone** where you're going and when
- **Bring a friend** if possible
- **Inspect the item** thoroughly before paying
- **Trust your instincts** - leave if something feels wrong
- **Cash transactions** - count money carefully`
      },
    ]
  },

  'trust-scores': {
    title: 'Understanding Trust Scores',
    subtitle: 'How reputation works on WANTD',
    icon: CheckCircle,
    sections: [
      {
        title: 'What is a Trust Score?',
        content: `Your trust score reflects your reliability on WANTD. Higher scores mean:
- More visibility in search results
- Better match priority
- Increased buyer/seller confidence`
      },
      {
        title: 'How to Build Trust',
        content: `**Positive factors:**
- Verified account
- Active, accurate listings
- Positive interactions with other users
- Maintaining a good track record

**Negative factors:**
- Failed meetups
- Misrepresented items
- Reports from other users`
      }
    ]
  },

  'report-issues': {
    title: 'Reporting Issues',
    subtitle: 'Help keep WANTD safe',
    icon: Shield,
    sections: [
      {
        title: 'What to Report',
        content: `- **Suspicious listings** - Scams, fake items
- **No-shows** - Users who don't show up to meetups
- **Misrepresentation** - Items not as described
- **Harassment** - Inappropriate messages
- **Prohibited items** - Things that shouldn't be sold`
      },
      {
        title: 'How to Report',
        content: `1. Go to the listing or user profile
2. Tap the **flag icon** or "Report"
3. Select the reason
4. Add details if needed
5. Submit

We review all reports and take action as needed.`
      }
    ]
  },

  // FAQ
  'general-faq': {
    title: 'General Questions',
    subtitle: 'Common questions about WANTD',
    icon: HelpCircle,
    sections: [
      {
        title: 'What is WANTD?',
        content: `WANTD is a local marketplace where you can browse items for sale near you, or post what you're looking for and let sellers come to you. Our intelligent matching system connects buyers with sellers in their area.`
      },
      {
        title: 'How is this different from other marketplaces?',
        content: `WANTD offers both traditional browsing AND the ability to post what you want. You can browse the catalog like any marketplace, or post a "want" with your price range and let sellers be notified when they have a match. It's like having a personal shopper working 24/7.`
      },
      {
        title: 'Is it free to use?',
        content: `WANTD is completely free to use. Creating an account, posting wants, listing items, and connecting with buyers or sellers costs nothing. Transactions are completed offline between users.`
      },
      {
        title: 'What areas does WANTD cover?',
        content: `WANTD is currently available across Australia. We're focused on local transactions, so you'll primarily see items and buyers in your area.`
      }
    ]
  },

  'account-faq': {
    title: 'Account & Privacy',
    subtitle: 'Your data and security',
    icon: Shield,
    sections: [
      {
        title: 'Is my personal information safe?',
        content: `Yes. Your exact location is never shared - only the suburb/area name. Personal contact details are only revealed after both parties agree to a transaction. We use industry-standard encryption for all data.`
      },
      {
        title: 'Can I delete my account?',
        content: `Yes. Go to **Profile** > **Settings** > **Delete Account**. This will:
- Remove all your listings
- Delete your personal data
- Cancel any active wants

Note: Some transaction history may be retained for legal/safety purposes.`
      },
      {
        title: 'How do I change my email or password?',
        content: `Go to **Profile** > **Settings** to update:
- Email address
- Password
- Notification preferences`
      }
    ]
  }
};

export default function HelpPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedSections, setExpandedSections] = useState(['getting-started', 'ai-features']);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get current guide from URL params, default to 'welcome'
  const currentGuideId = searchParams.get('guide') || 'welcome';
  const currentGuide = GUIDE_CONTENT[currentGuideId];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectGuide = (guideId) => {
    setSearchParams({ guide: guideId });
  };

  // Find which section contains the current guide
  const getCurrentSection = () => {
    for (const section of GUIDE_SECTIONS) {
      if (section.guides.some(g => g.id === currentGuideId)) {
        return section.id;
      }
    }
    return null;
  };

  // Auto-expand section containing current guide
  const currentSection = getCurrentSection();
  if (currentSection && !expandedSections.includes(currentSection)) {
    setExpandedSections(prev => [...prev, currentSection]);
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[var(--theme-bg)]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--theme-text-primary)]">Help Center</h1>
              <p className="text-[var(--theme-text-secondary)] mt-1">Guides, tips, and answers</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full bg-[var(--theme-card)] border border-[var(--theme-border)] flex items-center justify-center hover:bg-[var(--theme-hover)] transition-colors"
              data-testid="close-help-btn"
            >
              <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)] focus:outline-none focus:ring-2 focus:ring-[#C9956C]/30"
              data-testid="help-search-input"
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Navigation */}
            <aside className="lg:w-72 flex-shrink-0">
              <nav className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl overflow-hidden sticky top-6">
                {GUIDE_SECTIONS.map((section) => (
                  <div key={section.id} className="border-b border-[var(--theme-border)] last:border-b-0">
                    {/* Section Header */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--theme-hover)] transition-colors"
                      data-testid={`section-${section.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <section.icon className="w-5 h-5 text-[var(--theme-text-secondary)]" />
                        <span className="font-medium text-[var(--theme-text-primary)]">{section.title}</span>
                        {section.badge && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-[#C9956C]/10 text-[#C9956C] rounded-full">
                            {section.badge}
                          </span>
                        )}
                      </div>
                      <ChevronDown 
                        className={`w-4 h-4 text-[var(--theme-text-muted)] transition-transform ${
                          expandedSections.includes(section.id) ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    {/* Section Guides */}
                    {expandedSections.includes(section.id) && (
                      <div className="pb-2">
                        {section.guides.map((guide) => (
                          <button
                            key={guide.id}
                            onClick={() => selectGuide(guide.id)}
                            className={`w-full px-4 py-2 pl-12 text-left text-sm flex items-center gap-2 transition-colors ${
                              currentGuideId === guide.id
                                ? 'text-[#C9956C] bg-[#C9956C]/5 border-r-2 border-[#C9956C]'
                                : 'text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)]'
                            }`}
                            data-testid={`guide-${guide.id}`}
                          >
                            {guide.title}
                            {guide.isNew && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-[#059669]/10 text-[#059669] rounded">
                                New
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Quick Links */}
              <div className="mt-4 p-4 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl">
                <h3 className="font-medium text-[var(--theme-text-primary)] mb-3">Quick Links</h3>
                <div className="space-y-2">
                  <Link 
                    to="/about-us" 
                    className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)] hover:text-[#C9956C] transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                    About WANTD
                  </Link>
                  <Link 
                    to="/terms" 
                    className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)] hover:text-[#C9956C] transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Terms of Service
                  </Link>
                  <Link 
                    to="/privacy" 
                    className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)] hover:text-[#C9956C] transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {currentGuide ? (
                <article className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl overflow-hidden">
                  {/* Guide Header */}
                  <div className="p-6 md:p-8 border-b border-[var(--theme-border)]">
                    <div className="flex items-start gap-4">
                      {currentGuide.icon && (
                        <div className="w-12 h-12 rounded-xl bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0">
                          <currentGuide.icon className="w-6 h-6 text-[#C9956C]" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">{currentGuide.title}</h1>
                          {currentGuide.badge && (
                            <span className="px-2 py-1 text-xs font-medium bg-[#C9956C]/10 text-[#C9956C] rounded-full">
                              {currentGuide.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[var(--theme-text-secondary)]">{currentGuide.subtitle}</p>
                      </div>
                    </div>
                  </div>

                  {/* Guide Content */}
                  <div className="p-6 md:p-8 space-y-8">
                    {currentGuide.sections.map((section, index) => (
                      <div 
                        key={index} 
                        className={section.highlight ? 'p-6 bg-[#C9956C]/5 border border-[#C9956C]/20 rounded-xl' : ''}
                      >
                        {section.title && (
                          <h2 className="text-lg font-bold text-[var(--theme-text-primary)] mb-3 flex items-center gap-2">
                            {section.highlight && <Lightbulb className="w-5 h-5 text-[#C9956C]" />}
                            {section.title}
                          </h2>
                        )}
                        {section.content && (
                          <div className="prose prose-sm max-w-none text-[var(--theme-text-secondary)]">
                            {section.content.split('\n\n').map((paragraph, pIndex) => (
                              <p key={pIndex} className="mb-3 leading-relaxed whitespace-pre-line">
                                {paragraph.split('**').map((part, i) => 
                                  i % 2 === 1 
                                    ? <strong key={i} className="text-[var(--theme-text-primary)] font-semibold">{part}</strong>
                                    : part
                                )}
                              </p>
                            ))}
                          </div>
                        )}
                        {section.links && (
                          <div className="space-y-2 mt-4">
                            {section.links.map((link, lIndex) => (
                              <button
                                key={lIndex}
                                onClick={() => selectGuide(link.guide)}
                                className="flex items-center gap-2 text-[#C9956C] hover:underline"
                              >
                                <ChevronRight className="w-4 h-4" />
                                {link.text}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Guide Footer */}
                  <div className="p-6 md:p-8 border-t border-[var(--theme-border)] bg-[var(--theme-hover)]">
                    <p className="text-sm text-[var(--theme-text-muted)]">
                      Was this helpful? Have more questions?{' '}
                      <a href="mailto:support@wantd.au" className="text-[#C9956C] hover:underline">
                        Contact support
                      </a>
                    </p>
                  </div>
                </article>
              ) : (
                <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl p-8 text-center">
                  <HelpCircle className="w-12 h-12 text-[var(--theme-text-muted)] mx-auto mb-4" />
                  <p className="text-[var(--theme-text-secondary)]">Select a guide from the sidebar</p>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}
